import React from 'react';
import PropTypes from 'prop-types';
import s from './CompanionConfigVersionList.module.scss';
import { FormattedMessage, injectIntl } from 'react-intl';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import { Link } from 'react-router-dom';
import moment from 'moment-timezone';

const COLUMN_COUNT = 7;

const CompanionConfigVersionListTable = ({
  paginatedData = [],
  currentTimezone,
  hasLoadError,
  errorMessageText,
  onRetry,
  ModelCategoryId,
  ModelId,
  isModelCategoryOwner,
  UpdateAllowed,
  DeleteAllowed,
  onDeleteClick
}) => {
  return (
    <div className={s.wsTableContainer}>
      <Table variant className={s.wsTable}>
        <thead>
          <tr>
            <th>
              <FormattedMessage id="companionConfigVersion.name" defaultMessage="Name" />
            </th>
            <th>
              <FormattedMessage id="companionConfigVersion.version" defaultMessage="Version" />
            </th>
            <th>
              <FormattedMessage id="companionConfigVersion.status" defaultMessage="Status" />
            </th>
            <th className="d-none d-md-table-cell">
              <FormattedMessage id="companionConfigVersion.publishedBy" defaultMessage="Published By" />
            </th>
            <th className="d-none d-md-table-cell">
              <FormattedMessage id="companionConfigVersion.publishedDate" defaultMessage="Published Date" />
            </th>
            <th>
              <FormattedMessage id="companionConfigVersion.activeVersion" defaultMessage="Active Version" />
            </th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {hasLoadError && (
            <tr>
              <td colSpan={COLUMN_COUNT} className={s.stateRow}>
                <div className={s.stateTitle}>
                  <FormattedMessage id="companionConfigVersion.loadErrorTitle" defaultMessage="Unable to load companion app config versions" />
                </div>
                {errorMessageText && <div className={s.stateHint}>{errorMessageText}</div>}
                <Button variant="outline-secondary" className={s.retryButton} onClick={onRetry}>
                  <FormattedMessage id="companionConfigVersion.retry" defaultMessage="RETRY" />
                </Button>
              </td>
            </tr>
          )}
          {/* No records found -> leave blank, same as ModelFirmwareVersionListTable.jsx
              (no message/retry outside the genuine load-error case above). */}
          {!hasLoadError &&
            paginatedData.map(n => {
              // A published row is immutable (BCC-08) - only offer Edit/Delete
              // on Draft rows, matching what the backend actually allows.
              const isDraft = n.Status === 'Draft';
              return (
                <tr
                  key={n.ModelCompanionConfigVersionId}
                  className={n.IsCurrentVersion ? s.borderGreen : ''}
                  data-unittest="companionConfigVersionData">
                  <td>{n.Name || ''}</td>
                  <td>{n.VersionNumber || ''}</td>
                  <td className={s.status}>{n.Status || ''}</td>
                  <td className="d-none d-md-table-cell">{n.PublishedBy || '-'}</td>
                  <td className="d-none d-md-table-cell">
                    {n.PublishedDateTime
                      ? moment
                          .unix(n.PublishedDateTime)
                          .tz(currentTimezone)
                          .format('DD-MMM-YYYY  HH:mm:ss z')
                      : '-'}
                  </td>
                  <td>
                    {n.IsCurrentVersion ? (
                      <span className={`badge rounded-pill ${s.badgeColor}`}>
                        <FormattedMessage id="companionConfigVersion.active" defaultMessage="Active" />
                      </span>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {isDraft && UpdateAllowed && isModelCategoryOwner && (
                      <Link
                        to={`/operational-setup/model-category-configuration/${ModelCategoryId}/model-configuration/${ModelId}/companion-config-version/edit/${n.ModelCompanionConfigVersionId}`}>
                        <Button variant="outline-secondary" className={s.changeButton}>
                          <FormattedMessage id="companionConfigVersion.edit" defaultMessage="EDIT" />
                        </Button>
                      </Link>
                    )}
                    {isDraft && DeleteAllowed && isModelCategoryOwner && (
                      <Button
                        variant="outline-secondary"
                        className={s.removeButton}
                        onClick={() => onDeleteClick(n.ModelCompanionConfigVersionId)}>
                        <FormattedMessage id="companionConfigVersion.delete" defaultMessage="DELETE" />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
        </tbody>
      </Table>
    </div>
  );
};

CompanionConfigVersionListTable.propTypes = {
  currentTimezone: PropTypes.string.isRequired,
  hasLoadError: PropTypes.bool,
  errorMessageText: PropTypes.string,
  onRetry: PropTypes.func,
  ModelCategoryId: PropTypes.string,
  ModelId: PropTypes.number,
  isModelCategoryOwner: PropTypes.bool,
  UpdateAllowed: PropTypes.bool,
  DeleteAllowed: PropTypes.bool,
  onDeleteClick: PropTypes.func
};

export default injectIntl(CompanionConfigVersionListTable);
